import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Search, Share2, TrendingUp, Target, Mail, BarChart3, Users, Zap, Crown, Rocket, Globe, MessageCircle } from 'lucide-react';
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const LeadGeneration = () => {
  const services = [
    {
      icon: <Search className="w-8 h-8" />,
      title: "SEO Services",
      description: "Ascend the digital throne with our SEO dream team. Our seasoned specialists wield their SEO sorcery to breathe life into your website, sending your search engine rankings soaring to new heights.",
      color: "bg-[#1E3A8A]"
    },
    {
      icon: <Share2 className="w-8 h-8" />,
      title: "Social Media Marketing",
      description: "Experience an electrifying social media revolution. We engineer captivating experiences that ignite conversations, spark excitement, and turn heads like never before.",
      color: "bg-[#EA580C]"
    },
    {
      icon: <Mail className="w-8 h-8" />,
      title: "Email Marketing",
      description: "Strategic email campaigns that convert. From nurture sequences to promotional blasts, we craft messages that resonate and drive action.",
      color: "bg-[#0D9488]"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "PPC & Paid Advertising",
      description: "Maximize ROI with precision-targeted paid campaigns across Google, Facebook, Instagram, and LinkedIn. Every dollar optimized for results.",
      color: "bg-[#DC2626]"
    }
  ];

  const seoFeatures = [
    "Keyword Research & Strategy",
    "On-Page SEO Optimization",
    "Technical SEO Audits",
    "Link Building Campaigns",
    "Local SEO Domination",
    "Content Optimization",
    "Competitor Analysis",
    "Monthly Performance Reports"
  ];

  const socialFeatures = [
    "Platform Strategy & Planning",
    "Content Creation & Curation",
    "Community Management",
    "Influencer Partnerships",
    "Paid Social Campaigns",
    "Analytics & Reporting",
    "Brand Voice Development",
    "Viral Content Engineering"
  ];

  const results = [
    { metric: "350%", label: "Average Traffic Increase" },
    { metric: "10x", label: "Lead Generation Boost" },
    { metric: "45%", label: "Conversion Rate Improvement" },
    { metric: "500+", label: "Campaigns Launched" }
  ];

  return (
    <div data-testid="lead-generation-page">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#0D9488] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 right-20 w-96 h-96 bg-[#EA580C] rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-20 w-72 h-72 bg-white rounded-full blur-3xl" />
        </div>
        <div className="container-max mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block px-4 py-1.5 bg-white/10 text-white text-xs font-bold uppercase tracking-wider rounded-full mb-6">
              Your Vision, Our Strategies, We Win Together
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white mb-6">
              Lead Generation &{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#EA580C] to-yellow-400">
                Business Development
              </span>
            </h1>
            <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
              Boost your business with Prestigious Marketing Inc. From social media management 
              to email marketing campaigns, our team develops personalized strategies tailored 
              to your specific needs.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/partner">
                <Button 
                  size="lg" 
                  className="bg-[#EA580C] hover:bg-[#c2410c] text-white px-8 py-6 text-base"
                  data-testid="leadgen-cta-partner"
                >
                  Grow Your Business
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-base"
                >
                  Find Out More
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-[#0F172A] py-12 border-t border-slate-800">
        <div className="container-max mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {results.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-2">
                  {stat.metric}
                </div>
                <div className="text-slate-400 text-sm font-medium uppercase tracking-wider">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Digital Marketing Services Overview */}
      <section className="section-padding bg-white">
        <div className="container-max mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">
              Digital Marketing Services
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              Grow Your Business with Prestigious Marketing Inc
            </h2>
            <p className="text-slate-600 max-w-3xl mx-auto">
              We understand that every business is unique, which is why we take the time to get 
              to know your brand and target audience. With Prestigious Marketing Inc, you can 
              rest assured that your marketing efforts are in good hands.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, idx) => (
              <Card key={idx} className="border-slate-200 card-hover" data-testid={`service-card-${idx}`}>
                <CardContent className="p-6 text-center">
                  <div className={`w-16 h-16 ${service.color} text-white rounded-2xl flex items-center justify-center mx-auto mb-5`}>
                    {service.icon}
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Services Section */}
      <section className="section-padding bg-slate-50" data-testid="seo-section">
        <div className="container-max mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-[#1E3A8A] text-white rounded-xl flex items-center justify-center">
                  <Crown className="w-6 h-6" />
                </div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                  SEO Services & More
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-6">
                Ascend the Digital Throne
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Get ready to ascend the digital throne with Prestigious Marketing's SEO dream team. 
                Our seasoned specialists are masters of the digital realm, wielding their SEO sorcery 
                to breathe life into your website.
              </p>
              <p className="text-slate-600 mb-6 leading-relaxed">
                With precision and finesse, we'll unleash the power of optimization, sending your 
                search engine rankings soaring to new heights. Watch your website conquer the search 
                results, claiming the top spot like a triumphant ruler.
              </p>
              <p className="text-slate-600 mb-8 leading-relaxed">
                Brace yourself for an onslaught of organic traffic, a torrent of eager visitors 
                flocking to your online domain. From keyword alchemy to content enchantment, our 
                SEO wizards will work their wonders, and the world will bow before your online prowess.
              </p>
              <Link to="/partner">
                <Button className="bg-[#1E3A8A] hover:bg-[#1e40af]" data-testid="seo-cta">
                  Seize the Throne
                  <Crown className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
              <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                <Search className="w-5 h-5 text-[#1E3A8A]" />
                Our SEO Arsenal
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {seoFeatures.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-[#0D9488]" />
                    <span className="text-slate-700 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media Marketing Section */}
      <section className="section-padding bg-gradient-to-br from-[#EA580C] to-[#DC2626]" data-testid="social-section">
        <div className="container-max mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="order-2 lg:order-1 bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                Social Media Mastery
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {socialFeatures.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-white" />
                    <span className="text-white/90 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white/20 text-white rounded-xl flex items-center justify-center">
                  <Rocket className="w-6 h-6" />
                </div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-white/80">
                  Social Media Marketing
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-6">
                Social Media Marketing That Rocks
              </h2>
              <p className="text-white/90 mb-6 leading-relaxed">
                Prepare for an electrifying social media revolution, led by Prestigious Marketing's 
                dynamic dream team. We don't just create social media campaigns; we engineer 
                captivating experiences that leave your audience spellbound.
              </p>
              <p className="text-white/90 mb-6 leading-relaxed">
                With our strategic brilliance and creative prowess, your social media presence will 
                ignite like a wildfire, igniting conversations, sparking excitement, and turning 
                heads like never before. Get ready for a tsunami of engagement!
              </p>
              <p className="text-white/90 mb-8 leading-relaxed">
                Brace yourself for an avalanche of leads and sales, as your online empire flourishes 
                under our guidance. From irresistible visuals to compelling storytelling, we'll 
                masterfully orchestrate every element to drive unprecedented growth and success.
              </p>
              <Link to="/partner">
                <Button 
                  className="bg-white text-[#EA580C] hover:bg-white/90"
                  data-testid="social-cta"
                >
                  Set the World Ablaze
                  <Zap className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-white">
        <div className="container-max mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">
              Our Process
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              How We Drive Results
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "01", icon: <Target />, title: "Discovery", desc: "We analyze your business, competitors, and target audience." },
              { step: "02", icon: <BarChart3 />, title: "Strategy", desc: "Craft a data-driven marketing plan tailored to your goals." },
              { step: "03", icon: <Rocket />, title: "Execute", desc: "Launch campaigns with precision and creative excellence." },
              { step: "04", icon: <TrendingUp />, title: "Optimize", desc: "Continuously refine for maximum ROI and growth." }
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="w-16 h-16 bg-[#1E3A8A] text-white rounded-2xl flex items-center justify-center mx-auto mb-4">
                  {item.icon}
                </div>
                <div className="text-4xl font-black text-[#EA580C]/20 mb-2">
                  {item.step}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-max mx-auto text-center">
          <blockquote className="max-w-3xl mx-auto">
            <p className="text-2xl sm:text-3xl font-light text-slate-700 italic mb-6">
              "Marketing is really just about sharing your passion."
            </p>
            <cite className="text-slate-500 not-italic font-medium">— Michael Hyatt</cite>
          </blockquote>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-gradient py-20">
        <div className="container-max mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-black text-white mb-6">
            Ready to Dominate Your Market?
          </h2>
          <p className="text-slate-300 mb-10 max-w-2xl mx-auto">
            Welcome to the forefront of the digital frontier, where innovation meets impact. 
            Together, we'll command the digital landscape, captivating hearts and dominating the conversation.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/partner">
              <Button 
                size="lg" 
                className="bg-[#EA580C] hover:bg-[#c2410c] text-white px-8"
                data-testid="leadgen-final-cta"
              >
                Let's Ascend to Greatness
                <Crown className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                size="lg" 
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 px-8"
              >
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LeadGeneration;
